import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Loader, Empty, Stat, DataTag } from "@/components/widgets";
import { Target, ArrowClockwise } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

export default function Scorecard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => { setLoading(true); api.get("/forecasts/scorecard").then((r) => setData(r.data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  if (!data) return <Loader label="Loading forecast track record" />;

  const dirColor = (d) => d === "bullish" ? "pos" : d === "bearish" ? "neg" : "text-muted-foreground";

  return (
    <div>
      <div className="flex items-end justify-between">
        <div><div className="label-xs">Intelligence Layer</div><h1 className="mt-1 font-heading text-3xl font-bold tracking-tighter flex items-center gap-2"><Target size={26} className="text-volt" /> Forecast Scorecard</h1></div>
        <div className="flex items-center gap-3">
          <DataTag kind="MODEL" />
          <Button data-testid="refresh-scorecard" onClick={load} disabled={loading} variant="outline" className="rounded-sm border-border gap-2"><ArrowClockwise size={15} /> Resolve</Button>
        </div>
      </div>

      <p className="mt-3 text-sm text-muted-foreground max-w-2xl">
        Every forecast the engine makes is logged, then scored against what actually happened. This is the model's real,
        self-updating directional hit-rate — transparency over hype. Random chance for a 3-way (bull/neutral/bear) call is ~33%.
      </p>

      <div className="mt-5 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="border border-border bg-card p-6 flex flex-col items-center justify-center">
          <div className="label-xs">Overall Hit-Rate</div>
          <div className="mono text-5xl font-semibold ai-glow mt-2" style={{ color: "#007AFF" }} data-testid="overall-accuracy">{data.overall.accuracy}%</div>
          <div className="mono text-xs text-muted-foreground mt-1">{data.overall.correct} / {data.overall.total} resolved</div>
        </div>
        {["1d", "5d", "20d"].map((h) => (
          <HorizonCard key={h} h={h} d={data.by_horizon[h]} />
        ))}
      </div>

      {data.pending > 0 && <div className="mt-3 mono text-xs text-muted-foreground">{data.pending} forecast(s) pending resolution.</div>}

      {data.by_confidence && (
        <div className="mt-5">
          <div className="label-xs mb-3">Hit-Rate by Model Confidence</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {["high", "medium", "low"].map((b) => (
              <ConfidenceCard key={b} bucket={b} d={data.by_confidence[b]} />
            ))}
          </div>
          <p className="mt-2 mono text-[11px] text-muted-foreground">Calibration check: if higher confidence doesn't yield a higher hit-rate, treat the confidence score as directional only — a transparent look at where the model is (and isn't) reliable.</p>
        </div>
      )}

      <div className="mt-5 border border-border bg-card">
        <div className="border-b border-border px-4 py-3 label-xs">Recently Resolved Forecasts</div>
        {data.recent.length === 0 ? <Empty>No resolved forecasts yet</Empty> : (
          <>
            <div className="grid grid-cols-[70px_60px_1fr_1fr_90px_90px] gap-2 px-4 py-2 label-xs border-b border-border/50">
              <span>Ticker</span><span>Horizon</span><span>Predicted</span><span>Actual</span><span className="text-right">Return</span><span className="text-right">Result</span>
            </div>
            {data.recent.map((r, i) => (
              <div key={i} className="grid grid-cols-[70px_60px_1fr_1fr_90px_90px] gap-2 items-center px-4 py-2.5 border-b border-border/50 text-sm">
                <span className="mono font-medium">{r.ticker}</span>
                <span className="mono text-xs text-muted-foreground">{r.horizon}</span>
                <span className={`mono text-xs uppercase ${dirColor(r.pred_direction)}`}>{r.pred_direction}</span>
                <span className={`mono text-xs uppercase ${dirColor(r.actual_direction)}`}>{r.actual_direction}</span>
                <span className={`mono text-right text-xs ${r.actual_return >= 0 ? "pos" : "neg"}`}>{r.actual_return >= 0 ? "+" : ""}{r.actual_return}%</span>
                <span className={`mono text-right text-[11px] uppercase ${r.status === "correct" ? "pos" : "neg"}`}>{r.status === "correct" ? "✓ Hit" : "✗ Miss"}</span>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

function HorizonCard({ h, d }) {
  return (
    <div className="border border-border bg-card p-5" data-testid={`horizon-${h}`}>
      <div className="label-xs">{h.toUpperCase()} Horizon</div>
      <div className="mono text-3xl font-semibold mt-2">{d.accuracy}%</div>
      <div className="mt-3 h-2 w-full bg-white/10 rounded-sm overflow-hidden border border-dashed border-white/10">
        <div className="h-full bg-volt" style={{ width: `${d.accuracy}%`, transition: "width 0.6s ease" }} />
      </div>
      <div className="mono text-xs text-muted-foreground mt-2">{d.correct} / {d.total} correct</div>
    </div>
  );
}

function ConfidenceCard({ bucket, d }) {
  const color = bucket === "high" ? "#34C759" : bucket === "medium" ? "#FFCC00" : "#8A8A93";
  const range = bucket === "high" ? "conf ≥ 0.60" : bucket === "medium" ? "0.48 – 0.60" : "conf < 0.48";
  return (
    <div className="border border-border bg-card p-5" data-testid={`confidence-${bucket}`}>
      <div className="flex items-center justify-between">
        <span className="label-xs capitalize">{bucket} Confidence</span>
        <span className="mono text-[10px] text-muted-foreground">{range}</span>
      </div>
      <div className="mono text-3xl font-semibold mt-2" style={{ color }}>{d.accuracy}%</div>
      <div className="mt-3 h-2 w-full bg-white/10 rounded-sm overflow-hidden">
        <div className="h-full" style={{ width: `${d.accuracy}%`, background: color, transition: "width 0.6s ease" }} />
      </div>
      <div className="mono text-xs text-muted-foreground mt-2">{d.correct} / {d.total} correct</div>
    </div>
  );
}
